'use client';

import { cn } from '@core/utils';
import { AnimatePresence, motion } from 'framer-motion';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import { Image } from '@/components/client';
import { WELLO_IMAGE_PREFIX_URL } from '@/constants';

import { ANIMATION_CONFIG } from '../constants';

const QUICK_MENU_BASE_URL = `${WELLO_IMAGE_PREFIX_URL}/donation/event/stamp`;

const QUICK_IMAGES = [
  `${QUICK_MENU_BASE_URL}/quick1.png`,
  `${QUICK_MENU_BASE_URL}/quick2.png`,
  `${QUICK_MENU_BASE_URL}/quick3.png`,
  `${QUICK_MENU_BASE_URL}/quick4.png`,
] as const;

const QUICK_TEXT_IMAGES = [
  `${QUICK_MENU_BASE_URL}/quick-text1.png`,
  `${QUICK_MENU_BASE_URL}/quick-text2.png`,
] as const;

const STORAGE_KEY = 'prize-quick-menu-hidden';

type PrizeQuickMenuProps = {
  href: string;
  className?: string;
};

export const PrizeQuickMenu = ({ href, className }: PrizeQuickMenuProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    const hidden = sessionStorage.getItem(STORAGE_KEY);

    if (hidden === 'true') {
      setIsHidden(true);
    }
  }, []);

  useEffect(() => {
    if (isHidden) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % QUICK_IMAGES.length);
    }, ANIMATION_CONFIG.carouselRotationIntervalMs);

    return () => clearInterval(interval);
  }, [isHidden]);

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    sessionStorage.setItem(STORAGE_KEY, 'true');
    setIsHidden(true);
  };

  if (isHidden) {
    return null;
  }

  const currentImage = QUICK_IMAGES[currentIndex];
  const currentTextImage = QUICK_TEXT_IMAGES[currentIndex % 2];

  return (
    <div className={cn('relative size-fit', className)}>
      <Link href={href} className="block w-fit" aria-label="경품 안내 바로가기">
        <div className="relative flex h-94 w-78 flex-col items-center justify-center gap-4 rounded-medium border-[3px] border-solid border-Y-200 bg-Y-100">
          <div className="relative size-50">
            <AnimatePresence>
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{
                  duration: ANIMATION_CONFIG.transitionDurationSeconds,
                  ease: ANIMATION_CONFIG.easingCurve,
                }}
                className="absolute inset-0 flex size-50 items-center justify-center"
              >
                <Image
                  src={currentImage}
                  alt="경품 이미지"
                  width={50}
                  height={50}
                  className="max-h-full max-w-full object-contain"
                  priority
                />
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="relative h-20 w-full">
            <AnimatePresence>
              <motion.div
                key={`text-${currentIndex % 2}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{
                  duration: ANIMATION_CONFIG.transitionDurationSeconds,
                  ease: ANIMATION_CONFIG.easingCurve,
                }}
                className="absolute inset-0 flex-center"
              >
                <Image
                  src={currentTextImage}
                  alt="경품 텍스트"
                  width={40}
                  height={20}
                  className="object-contain"
                  priority
                />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Link>
      <button
        type="button"
        onClick={handleClose}
        className="absolute -right-8 -top-8 z-[1] flex size-24 items-center justify-center rounded-pill transition-colors hover:bg-Gray-50"
        aria-label="퀵메뉴 닫기"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M12 23C5.92486 23 1 18.0751 1 12C1 5.92487 5.92486 1 12 1C18.0751 1 23 5.92487 23 12C23 18.0751 18.0751 23 12 23Z"
            fill="#E5E7EC"
          />
          <path
            d="M16.3467 7.80508C16.553 8.01412 16.5508 8.35087 16.3418 8.55723L12.8571 11.997L16.3418 15.4367C16.5508 15.6431 16.553 15.9799 16.3467 16.1889C16.1403 16.3979 15.8035 16.4001 15.5945 16.1938L12.1 12.7443L8.60558 16.1938C8.39653 16.4001 8.05978 16.3979 7.85342 16.1889C7.64707 15.9799 7.64925 15.6431 7.8583 15.4367L11.3429 11.997L7.8583 8.55723C7.64925 8.35087 7.64707 8.01412 7.85342 7.80508C8.05978 7.59603 8.39653 7.59384 8.60558 7.8002L12.1 11.2496L15.5945 7.8002C15.8035 7.59384 16.1403 7.59603 16.3467 7.80508Z"
            fill="#535861"
          />
        </svg>
      </button>
    </div>
  );
};
